// Copyright 2018 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import {
    INotification,
    INotificationController,
    INotificationService,
    IProgressNotificationController,
    NotificationUrgency,
} from "services/Notification/INotificationService";

class Notification implements IProgressNotificationController {
    private disposed: boolean;

    private _message: string;
    private _urgency: NotificationUrgency;
    private _progress: number;
    private listener: () => void;

    public readonly source: string;

    @computedFrom("_message")
    public get message(): string {
        return this._message;
    }

    @computedFrom("_urgency")
    public get urgency(): NotificationUrgency {
        return this._urgency;
    }

    @computedFrom("_progress")
    public get progress(): number {
        return this._progress;
    }

    private onDispose: (notification: Notification) => void;

    constructor(source: string, message: string, onDispose: (notification: Notification) => void) {
        this.disposed = false;
        this.source = source;
        this.onDispose = onDispose;
        this._message = message;
        this._urgency = NotificationUrgency.Info;
    }

    public setMessage(message: string): void {
        this._message = message;
    }

    public setUrgency(urgency: NotificationUrgency): void {
        this._urgency = urgency;
    }

    public setProgress(progress: number, message?: string): void {
        this._progress = progress;
        if (message) {
            this._message = message;
        }
    }

    public setOnClickListener(listener: () => void): void {
        this.listener = listener;
    }

    public dispose(): void {
        this.onDispose(this);
        this.disposed = true;
    }

    public onClick(): void {
        if (this.listener) {
            this.listener();
        }
    }
}

export class NotificationService implements INotificationService {
    private _notifications: INotification[];

    @computedFrom("_notifications")
    public get notifications(): INotification[] {
        return this._notifications;
    }

    constructor() {
        this._notifications = [];
    }

    public registerNotification(
        source: string,
        message: string,
        urgency?: NotificationUrgency
    ): INotificationController {
        return this.registerProgressNotification(source, message, urgency);
    }

    public registerProgressNotification(
        source: string,
        message: string,
        urgency?: NotificationUrgency
    ): IProgressNotificationController {
        // Create new notification
        const notification = new Notification(source, message, this.disposeNotification);

        if (this.notifications.length > 0 && this.notifications[0].message === notification.message) return;

        if (urgency) notification.setUrgency(urgency);

        this._notifications.unshift(notification);
        return notification;
    }

    private disposeNotification = (notification: Notification): void => {
        const index = this._notifications.findIndex((x) => x == notification);
        if (index === -1) return;

        this._notifications.splice(index, 1);
    };
}
